/** Mantém só os dígitos, para comparar CNPJ numérico com ou sem máscara. */
const onlyDigits = (value: string): string => value.replace(/\D/g, "");

/**
 * Mantém só letras e números, em maiúsculas. Desde julho de 2026 o CNPJ é
 * alfanumérico nas 12 primeiras posições (ex.: `01.CDW.WLA/0001-32`); os dois
 * dígitos verificadores continuam numéricos. Para o CNPJ antigo, só de
 * números, o resultado é o mesmo de `onlyDigits`.
 */
const normalizeCNPJ = (value: string): string =>
  value.toUpperCase().replace(/[^0-9A-Z]/g, "");

const applyMask = (c: string): string =>
  `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12, 14)}`;

const formatCNPJ = (number: string): string => {
  const chars = normalizeCNPJ(number);

  if (chars.length < 14) return number;

  return applyMask(chars);
};

/**
 * Máscara progressiva para digitação: `12.345.678/0001-90` vai se formando a
 * cada tecla, sem esperar os 14 caracteres (o `formatCNPJ` acima só formata o
 * número completo — serve para exibir, não para digitar). Aceita letras nas
 * 12 primeiras posições; as duas últimas só aceitam dígito.
 */
const maskCNPJ = (value: string): string => {
  const raw = normalizeCNPJ(value).slice(0, 14);
  const d = raw.slice(0, 12) + raw.slice(12).replace(/\D/g, "");
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12)
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

/**
 * Valida o dígito verificador (módulo 11) do CNPJ numérico ou alfanumérico.
 * No alfanumérico cada caractere vale o código ASCII menos 48: dígito vale o
 * próprio número, "A" vale 17, "B" 18 … "Z" 42 — regra da Receita Federal.
 */
function isValidCNPJ(cnpj: string): boolean {
  const cleaned = normalizeCNPJ(cnpj);

  if (cleaned.length !== 14) return false;
  if (!/^[0-9A-Z]{12}\d{2}$/.test(cleaned)) return false;
  if (/^(.)\1+$/.test(cleaned)) return false;

  const calculateCheckDigit = (base: string, weights: number[]) => {
    const sum = base
      .split("")
      .reduce(
        (acc, char, index) => acc + (char.charCodeAt(0) - 48) * weights[index],
        0
      );
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const base = cleaned.slice(0, 12);
  const firstCheckDigit = calculateCheckDigit(
    base,
    [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
  );
  const secondCheckDigit = calculateCheckDigit(
    base + firstCheckDigit,
    [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
  );

  return (
    cleaned === base + firstCheckDigit.toString() + secondCheckDigit.toString()
  );
}

export const cnpjUtils = {
  onlyDigits,
  normalizeCNPJ,
  formatCNPJ,
  maskCNPJ,
  isValidCNPJ,
};
